/**
 * Adapters that translate v2 customer analytics API responses into the
 * shape the customer UI components consume (e.g. `first_name`/`last_name`
 * instead of `fullName`, `service.name` instead of `offeringName`,
 * `totalUniqueCustomers` instead of `totalUsers`). These UI-facing shapes
 * are consumed across several components (customer-details-section,
 * customer-metrics-section, prev-purchases-section, info-section,
 * customer-breadcrumb), so this is kept as a permanent internal adapter
 * layer rather than something to delete — reworking every consumer to
 * read the raw v2 wire format directly would be a separate, wider effort.
 */

type NormalizedCustomerMetrics = {
  totalUniqueCustomers: number;
  totalRevenue: {currency: string; amount: number}[];
  totalCalls: number;
  currency: string;
};

type V2CustomerMetricsResponse = {
  totalUsers?: number;
};

type V2CustomerItem = {
  fullName?: string;
  email?: string;
  lastPurchaseDate?: string;
};

type V2CustomersResponse = {
  data?: V2CustomerItem[];
  total?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
};

type V2CustomerOffering = {
  offeringName?: string;
  offeringType?: string;
  reference?: string;
  date?: string;
  amount?: number;
  currency?: string;
};

type V2CustomerMetricsPerCustomerResponse = {
  totalPurchases?: number;
  revenueByCurrency?: Record<string, number>;
};

type NormalizedPrevPurchase = {
  service: {name: string; callPriceCurrency: string};
  offeringType?: string;
  date: string;
  amount: number;
  currency: string;
  reference: string;
};

export function normalizeV2CustomerMetrics(
  v2Data: V2CustomerMetricsResponse
): NormalizedCustomerMetrics {
  return {
    totalUniqueCustomers: v2Data.totalUsers ?? 0,
    totalRevenue: [],
    totalCalls: 0,
    currency: "",
  };
}

export function normalizeV2CustomersList(v2Data: V2CustomersResponse): {
  transactions: {
    id: string;
    first_name: string;
    last_name: string;
    email_address: string;
    lastPurchaseDate: string;
    statusName: string;
  }[];
  total?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
} {
  const items = v2Data.data ?? [];
  const transactions = items.map((c) => {
    const fullName = c.fullName ?? "";
    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0] ?? "";
    const lastName = parts.slice(1).join(" ") ?? "";
    return {
      id: c.email ?? "",
      first_name: firstName,
      last_name: lastName,
      email_address: (c.email ?? "").toLowerCase(),
      lastPurchaseDate: c.lastPurchaseDate ?? "",
      statusName: "",
    };
  });
  return {
    transactions,
    total: v2Data.total,
    hasNextPage: v2Data.hasNextPage,
    hasPrevPage: v2Data.hasPrevPage,
  };
}

export function normalizeV2PrevPurchases(
  v2Data: V2CustomerOffering[]
): NormalizedPrevPurchase[] {
  return (v2Data ?? []).map((o) => ({
    service: {
      name: o.offeringName ?? "",
      callPriceCurrency: o.currency ?? "",
    },
    offeringType: o.offeringType ?? "",
    date: o.date ?? "",
    amount: o.amount ?? 0,
    currency: o.currency ?? "",
    reference: o.reference ?? "",
  }));
}

export function normalizeV2CustomerMetricsPerCustomer(
  v2Data: V2CustomerMetricsPerCustomerResponse
): {
  numberOfPurchases: number;
  totalVolume: number;
  revenueByCurrency: Record<string, number>;
} {
  const totalPurchases = v2Data.totalPurchases ?? 0;
  const revenueByCurrency = v2Data.revenueByCurrency ?? {};
  const totalVolume = Object.values(revenueByCurrency).reduce(
    (sum, val) => sum + Number(val),
    0
  );
  return {numberOfPurchases: totalPurchases, totalVolume, revenueByCurrency};
}
